import { useEffect, useRef, useState } from 'react';
import {
  IconActivity, IconAlertCircle, IconClock, IconCash, IconUsers,
  IconChefHat, IconShieldHalf, IconRefresh, IconSparkles,
  IconCircleCheck, IconBolt, IconMail,
} from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import AdminLayout from '../../components/layout/AdminLayout';

/**
 * Live ops monitor — Phase 4.10.
 *
 * One page, auto-polled every 10s. Renders everything from a single
 * GET /api/live snapshot. Designed for an iPad sitting on the manager's
 * desk during service.
 */

interface LiveSnapshot {
  serverTime: string;
  daySession: null | {
    id: string; openedAt: string; openedBy: { name: string; role: string };
    openingFloatCents: number; ageMin: number;
  };
  ordersByStatus: { status: string; count: number; totalCents: number }[];
  kitchen: {
    id: string; orderNumber: number; status: string; channel: string;
    cashier: string | null; placedAt: string; ageMin: number; totalCents: number;
  }[];
  activeSessions: {
    id: string; userName: string; role: string; stationName: string | null;
    lastSeenAt: string; idleMin: number;
  }[];
  drawer: {
    openingFloatCents: number; cashSalesCents: number; paidInCents: number;
    paidOutCents: number; expectedCashCents: number;
  };
  anomalies: { voidsToday: number; refundsToday: number; lowStockCount: number };
  recentEvents: {
    id: string; at: string; action: string; actorName: string | null;
    actorRole: string | null; targetType: string | null;
  }[];
  topProducts: { name: string; qty: number; totalCents: number }[];
}

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}
function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
}
function fmtAge(min: number) {
  if (min < 1)   return 'just now';
  if (min < 60)  return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${m}m`;
}

const STATUS_TONE: Record<string, string> = {
  PLACED:             'bg-info-soft text-info',
  PREPARING:          'bg-warning-soft text-warning',
  READY:              'bg-success-soft text-success',
  SERVED:             'bg-success-soft text-success',
  PENDING:            'bg-light text-gray-700',
  VOIDED:             'bg-danger-soft text-danger',
  CANCELLED:          'bg-danger-soft text-danger',
  REFUNDED:           'bg-danger-soft text-danger',
  PARTIALLY_REFUNDED: 'bg-danger-soft text-danger',
};

const ROLE_TONE: Record<string, string> = {
  OWNER:      'bg-purple-soft text-purple',
  ADMIN:      'bg-indigo-soft text-indigo',
  MANAGER:    'bg-primary-soft text-primary',
  SUPERVISOR: 'bg-info-soft text-info',
  CASHIER:    'bg-success-soft text-success',
};

export default function AdminLivePage() {
  const { auth } = useAuth();
  const [data, setData] = useState<LiveSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState<'weekly' | 'monthly' | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);
  const isAdminPlus = auth && ['ADMIN', 'OWNER'].includes(auth.role);

  async function sendReport(kind: 'weekly' | 'monthly') {
    setSending(kind);
    setToast(null);
    try {
      const r = await fetch(`/api/live/reports/${kind}`, { method: 'POST', credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setToast(`${kind === 'weekly' ? 'Weekly' : 'Monthly'} report queued — check your inbox in a minute.`);
      window.setTimeout(() => setToast(null), 5000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSending(null);
    }
  }

  async function load(silent = false) {
    if (!silent) setRefreshing(true);
    try {
      const r = await fetch('/api/live', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    void load();
    pollRef.current = window.setInterval(() => { void load(true); }, 10000);
    return () => { if (pollRef.current) window.clearInterval(pollRef.current); };
  }, []);

  const totalOrdersToday  = data?.ordersByStatus.reduce((n, g) => n + g.count, 0) ?? 0;
  const totalRevenueToday = data?.ordersByStatus
    .filter((g) => !['VOIDED', 'CANCELLED'].includes(g.status))
    .reduce((n, g) => n + g.totalCents, 0) ?? 0;
  const oldestKitchen = data?.kitchen[0];

  return (
    <AdminLayout
      title="Live operations"
      subtitle={data ? `Last refresh ${fmtTime(data.serverTime)} · auto-refreshes every 10s` : 'Loading…'}
      actions={
        <div className="inline-flex items-center gap-2">
          {isAdminPlus && (
            <>
              <button
                type="button"
                onClick={() => void sendReport('weekly')}
                disabled={sending !== null}
                className="h-10 px-3 inline-flex items-center gap-2 rounded-md bg-light border-2 border-border text-13 font-bold text-heading hover:border-primary disabled:opacity-50"
                title="Email me the weekly summary now"
              >
                <IconMail size={14} stroke={2.5} />
                {sending === 'weekly' ? 'Sending…' : 'Weekly'}
              </button>
              <button
                type="button"
                onClick={() => void sendReport('monthly')}
                disabled={sending !== null}
                className="h-10 px-3 inline-flex items-center gap-2 rounded-md bg-light border-2 border-border text-13 font-bold text-heading hover:border-primary disabled:opacity-50"
                title="Email me the monthly summary now"
              >
                <IconMail size={14} stroke={2.5} />
                {sending === 'monthly' ? 'Sending…' : 'Monthly'}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => void load()}
            disabled={refreshing}
            className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-13 font-bold hover:bg-primary-hover shadow-sm disabled:bg-gray-300"
          >
            <IconRefresh size={16} stroke={2.5} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      }
    >
      {toast && (
        <div className="rounded-md bg-success-soft border border-success/30 p-3 flex items-start gap-2 mb-5">
          <IconCircleCheck size={18} className="text-success flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-success">{toast}</p>
        </div>
      )}
      {error && (
        <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2 mb-5">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      {!data ? (
        <p className="text-13 italic text-gray-700">Loading the snapshot…</p>
      ) : (
        <>
          {/* Top KPI strip — 6 tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <KpiTile
              tone={data.daySession ? 'success' : 'danger'}
              label={data.daySession ? 'Day open' : 'Day closed'}
              value={data.daySession ? fmtAge(data.daySession.ageMin) : '—'}
              sub={data.daySession ? `by ${data.daySession.openedBy.name}` : 'no active session'}
              Icon={IconBolt}
            />
            <KpiTile tone="primary" label="Orders today"    value={totalOrdersToday}                       sub="all statuses" Icon={IconActivity} />
            <KpiTile tone="purple"  label="Revenue today"   value={fmtR(totalRevenueToday)}                sub="net of voids" Icon={IconSparkles} />
            <KpiTile tone="warning" label="In kitchen"      value={data.kitchen.length}                    sub={oldestKitchen ? `oldest ${fmtAge(oldestKitchen.ageMin)}` : 'all clear'} Icon={IconChefHat} />
            <KpiTile tone="info"    label="Active users"    value={data.activeSessions.length}             sub={data.activeSessions.length === 1 ? 'logged in now' : 'logged in now'} Icon={IconUsers} />
            <KpiTile
              tone={data.anomalies.voidsToday + data.anomalies.refundsToday > 0 ? 'danger' : 'success'}
              label="Voids · Refunds"
              value={`${data.anomalies.voidsToday} · ${data.anomalies.refundsToday}`}
              sub={data.anomalies.voidsToday + data.anomalies.refundsToday > 0 ? 'review audit log' : 'none today'}
              Icon={IconShieldHalf}
            />
          </div>

          {/* Two-column body */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {/* Kitchen */}
            <Panel title="In the kitchen" subtitle={`${data.kitchen.length} orders awaiting prep / ready`} icon={IconChefHat}>
              {data.kitchen.length === 0 ? (
                <EmptyRow icon={IconCircleCheck} title="All clear" subtitle="No tickets waiting" tone="success" />
              ) : (
                <ul className="divide-y divide-border-soft">
                  {data.kitchen.map((o) => (
                    <li key={o.id} className="py-2.5 flex items-center gap-3">
                      <div className="flex-shrink-0 w-12 h-12 rounded-md bg-light inline-flex items-center justify-center">
                        <p className="text-15 font-bold text-heading tabular-nums">#{o.orderNumber}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center px-2 h-5 rounded-md ${STATUS_TONE[o.status]} text-11 font-bold uppercase tracking-wider`}>
                            {o.status}
                          </span>
                          <span className="inline-flex items-center px-2 h-5 rounded-md bg-light border border-border-soft text-11 font-bold uppercase tracking-wider text-gray-700">
                            {o.channel.replace('_', ' ')}
                          </span>
                        </div>
                        <p className="text-12 font-medium text-gray-700 mt-1 truncate">
                          {o.cashier ? `by ${o.cashier}` : 'unassigned'} · {fmtTime(o.placedAt)}
                        </p>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <p className={`text-13 font-bold tabular-nums ${o.ageMin >= 15 ? 'text-danger' : o.ageMin >= 8 ? 'text-warning' : 'text-heading'}`}>
                          {fmtAge(o.ageMin)}
                        </p>
                        <p className="text-11 font-semibold text-gray-700">{fmtR(o.totalCents)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            {/* Active users */}
            <Panel title="Who's logged in" subtitle="Sessions active in the last 5 min" icon={IconUsers}>
              {data.activeSessions.length === 0 ? (
                <EmptyRow icon={IconUsers} title="Nobody's here" subtitle="No active sessions right now" />
              ) : (
                <ul className="divide-y divide-border-soft">
                  {data.activeSessions.map((s) => (
                    <li key={s.id} className="py-2.5 flex items-center gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-[#FFFFFF] inline-flex items-center justify-center text-13 font-bold">
                        {s.userName.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-14 font-bold text-heading truncate">{s.userName}</p>
                          <span className={`inline-flex items-center px-2 h-5 rounded-md ${ROLE_TONE[s.role] ?? 'bg-light text-gray-700'} text-10 font-bold uppercase tracking-wider`}>
                            {s.role}
                          </span>
                        </div>
                        <p className="text-12 font-medium text-gray-700 mt-0.5 truncate">
                          {s.stationName ?? 'no station'} · last seen {s.idleMin < 1 ? 'just now' : `${s.idleMin}m ago`}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>

          {/* Drawer + Top products */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <Panel title="Cash drawer" subtitle="Expected cash right now if you opened it" icon={IconCash}>
              <div className="space-y-2">
                <DrawerRow label="Opening float"  value={fmtR(data.drawer.openingFloatCents)} />
                <DrawerRow label="Cash sales"     value={fmtR(data.drawer.cashSalesCents)}      tone="success" />
                <DrawerRow label="Paid in"        value={fmtR(data.drawer.paidInCents)}         tone="success" />
                <DrawerRow label="Paid out"       value={fmtR(data.drawer.paidOutCents)}        tone="danger"  prefix="−" />
                <div className="border-t-2 border-border pt-2 mt-2 flex items-center justify-between">
                  <p className="text-13 font-bold uppercase tracking-wider text-heading">Expected in drawer</p>
                  <p className="text-22 font-bold tabular-nums text-heading">{fmtR(data.drawer.expectedCashCents)}</p>
                </div>
              </div>
            </Panel>

            <Panel title="Top sellers today" subtitle={`Ranked by units sold`} icon={IconSparkles}>
              {data.topProducts.length === 0 ? (
                <EmptyRow icon={IconActivity} title="No sales yet" subtitle="Top products will populate as orders close" />
              ) : (
                <ul className="divide-y divide-border-soft">
                  {data.topProducts.map((p, i) => (
                    <li key={p.name + i} className="py-2.5 flex items-center gap-3">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-md inline-flex items-center justify-center text-12 font-bold ${i === 0 ? 'bg-warning text-[#FFFFFF]' : 'bg-light text-gray-700'}`}>
                        #{i + 1}
                      </div>
                      <p className="flex-1 min-w-0 text-14 font-bold text-heading truncate">{p.name}</p>
                      <p className="text-13 font-bold tabular-nums text-heading">{p.qty}<span className="text-11 text-gray-700 font-semibold ml-1">sold</span></p>
                      <p className="text-12 font-semibold text-gray-700 tabular-nums w-20 text-right">{fmtR(p.totalCents)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>

          {/* Recent activity */}
          <Panel title="Recent activity" subtitle="Last 15 audit events today" icon={IconClock}>
            {data.recentEvents.length === 0 ? (
              <EmptyRow icon={IconClock} title="Nothing yet today" subtitle="Audit events will appear as actions are taken" />
            ) : (
              <ul className="divide-y divide-border-soft">
                {data.recentEvents.map((e) => (
                  <li key={e.id} className="py-2.5 flex items-center gap-3">
                    <p className="flex-shrink-0 text-12 font-semibold text-gray-700 tabular-nums w-16">{fmtTime(e.at)}</p>
                    <span className="inline-flex items-center px-2 h-5 rounded-md bg-light border border-border-soft text-11 font-bold uppercase tracking-wider text-heading flex-shrink-0">
                      {e.action.replace(/_/g, ' ')}
                    </span>
                    <p className="flex-1 min-w-0 text-13 font-medium text-heading truncate">
                      {e.actorName ?? 'system'} {e.targetType && <span className="text-gray-700">· {e.targetType}</span>}
                    </p>
                    {e.actorRole && (
                      <span className={`inline-flex items-center px-2 h-5 rounded-md ${ROLE_TONE[e.actorRole] ?? 'bg-light text-gray-700'} text-10 font-bold uppercase tracking-wider`}>
                        {e.actorRole}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </>
      )}
    </AdminLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Bits                                                                        */
/* -------------------------------------------------------------------------- */

function KpiTile({ tone, label, value, sub, Icon }: {
  tone: 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'purple';
  label: string; value: number | string; sub: string; Icon: typeof IconActivity;
}) {
  const cls = {
    primary: { soft: 'bg-primary-soft', solid: 'bg-primary' },
    info:    { soft: 'bg-info-soft',    solid: 'bg-info' },
    success: { soft: 'bg-success-soft', solid: 'bg-success' },
    warning: { soft: 'bg-warning-soft', solid: 'bg-warning' },
    danger:  { soft: 'bg-danger-soft',  solid: 'bg-danger' },
    purple:  { soft: 'bg-purple-soft',  solid: 'bg-purple' },
  }[tone];
  return (
    <div className={`rounded-lg ${cls.soft} p-4 flex items-center justify-between gap-3`}>
      <div className="flex-1 min-w-0">
        <p className="text-24 font-bold text-heading tabular-nums leading-none truncate">{value}</p>
        <p className="text-13 font-semibold text-gray-700 mt-1 truncate">{label}</p>
        <p className="text-11 font-medium text-gray-600 mt-0.5 truncate">{sub}</p>
      </div>
      <div className={`w-10 h-10 rounded-lg ${cls.solid} inline-flex items-center justify-center flex-shrink-0`}>
        <Icon size={20} stroke={2.25} className="text-[#FFFFFF]" />
      </div>
    </div>
  );
}

function Panel({ title, subtitle, icon: Icon, children }: { title: string; subtitle: string; icon: typeof IconActivity; children: React.ReactNode }) {
  return (
    <section className="rounded-lg bg-surface border border-border p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-md bg-light inline-flex items-center justify-center">
          <Icon size={16} stroke={2} className="text-heading" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-15 font-bold text-heading leading-tight">{title}</h2>
          <p className="text-12 font-medium text-gray-700 truncate">{subtitle}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

function DrawerRow({ label, value, tone, prefix }: { label: string; value: string; tone?: 'success' | 'danger'; prefix?: string }) {
  const cls = tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-danger' : 'text-heading';
  return (
    <div className="flex items-center justify-between text-13">
      <p className="font-medium text-gray-700">{label}</p>
      <p className={`font-bold tabular-nums ${cls}`}>{prefix}{value}</p>
    </div>
  );
}

function EmptyRow({ icon: Icon, title, subtitle, tone }: { icon: typeof IconActivity; title: string; subtitle: string; tone?: 'success' }) {
  const cls = tone === 'success' ? 'text-success' : 'text-gray-700';
  return (
    <div className="text-center py-6">
      <div className={`w-10 h-10 rounded-2xl bg-light inline-flex items-center justify-center mb-2 ${cls}`}>
        <Icon size={20} stroke={2} />
      </div>
      <p className="text-13 font-bold text-heading">{title}</p>
      <p className="text-12 font-medium text-gray-700 mt-0.5">{subtitle}</p>
    </div>
  );
}
